import React, { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, Calendar, Mail, User, Clock, AlertTriangle, CheckCircle, XCircle, Loader2, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { apiUrl } from '../../utils/config';
import logger from '../../utils/logger';

const SubscriptionsAdmin = ({ onBack }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, expiring, expired

  useEffect(() => {
    fetchSubscriptions();
  }, [filter]);

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    try {
      let url = apiUrl('subscriptions');
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setSubscriptions(data);
    } catch (error) {
      logger.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const sendReminders = async () => {
    setIsSendingReminders(true);
    try {
      const response = await fetch(apiUrl('subscriptions/send-reminders'), {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Reminders sent: ${data.reminders_sent['7_days']} (7-day), ${data.reminders_sent['3_days']} (3-day), ${data.reminders_sent['1_day']} (1-day)`);
        fetchSubscriptions();
      } else {
        toast.error('Failed to send reminders');
      }
    } catch (error) {
      logger.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setIsSendingReminders(false);
    }
  };

  const renewSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(apiUrl(`subscriptions/${subscriptionId}/renew`), {
        method: 'PUT'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Subscription renewed successfully');
        fetchSubscriptions();
      } else {
        toast.error('Failed to renew subscription');
      }
    } catch (error) {
      logger.error('Error renewing subscription:', error);
      toast.error('Failed to renew subscription');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expiring': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'expiring': return <AlertTriangle className="w-4 h-4" />;
      case 'expired': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const activeCount = subscriptions.filter(s => s.status === 'active').length;
  const expiringCount = subscriptions.filter(s => {
    const days = getDaysRemaining(s.endDate);
    return s.status === 'active' && days <= 7 && days > 0;
  }).length;
  const expiredCount = subscriptions.filter(s => s.status === 'expired').length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
              <p className="text-gray-500">Manage customer subscriptions and send reminders</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={sendReminders}
              disabled={isSendingReminders}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSendingReminders ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Send Reminders
            </Button>
            <Button
              onClick={fetchSubscriptions}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div 
            className={`bg-white p-4 rounded-lg shadow-sm cursor-pointer border-2 ${filter === 'all' ? 'border-blue-500' : 'border-transparent'}`}
            onClick={() => setFilter('all')}
          >
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{subscriptions.length}</p>
          </div>
          <div 
            className={`bg-white p-4 rounded-lg shadow-sm cursor-pointer border-2 ${filter === 'active' ? 'border-green-500' : 'border-transparent'}`}
            onClick={() => setFilter('active')}
          >
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          </div>
          <div 
            className={`bg-white p-4 rounded-lg shadow-sm cursor-pointer border-2 ${filter === 'expiring' ? 'border-yellow-500' : 'border-transparent'}`}
            onClick={() => setFilter('all')}
          >
            <p className="text-sm text-gray-500">Expiring (≤7 days)</p>
            <p className="text-2xl font-bold text-yellow-600">{expiringCount}</p>
          </div>
          <div 
            className={`bg-white p-4 rounded-lg shadow-sm cursor-pointer border-2 ${filter === 'expired' ? 'border-red-500' : 'border-transparent'}`}
            onClick={() => setFilter('expired')}
          >
            <p className="text-sm text-gray-500">Expired</p>
            <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
          </div>
        </div>

        {/* Subscriptions List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center p-12">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No subscriptions found</p>
              <p className="text-sm text-gray-400 mt-1">Subscriptions will appear here after customers purchase subscription services</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reminders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {subscriptions.map((sub) => {
                    const daysRemaining = getDaysRemaining(sub.endDate);
                    const isExpiringSoon = daysRemaining <= 7 && daysRemaining > 0;
                    
                    return (
                      <tr key={sub.id} className={isExpiringSoon ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-3">
                              <p className="font-medium text-gray-900">{sub.customerName}</p>
                              <p className="text-sm text-gray-500">{sub.customerEmail}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{sub.serviceName}</p>
                          <p className="text-sm text-gray-500">₺{sub.amount}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="capitalize text-sm text-gray-600">{sub.billingCycle}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>
                            {getStatusIcon(sub.status)}
                            {sub.status}
                          </span>
                          {isExpiringSoon && (
                            <p className="text-xs text-yellow-600 mt-1">{daysRemaining} days left</p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(sub.endDate)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            <span className={`w-2 h-2 rounded-full ${sub.reminder7DaysSent ? 'bg-green-500' : 'bg-gray-300'}`} title="7-day reminder" />
                            <span className={`w-2 h-2 rounded-full ${sub.reminder3DaysSent ? 'bg-green-500' : 'bg-gray-300'}`} title="3-day reminder" />
                            <span className={`w-2 h-2 rounded-full ${sub.reminder1DaySent ? 'bg-green-500' : 'bg-gray-300'}`} title="1-day reminder" />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {sub.status === 'expired' && (
                            <Button
                              size="sm"
                              onClick={() => renewSubscription(sub.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Renew
                            </Button>
                          )}
                          {sub.status === 'active' && (
                            <a
                              href={`mailto:${sub.customerEmail}`}
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              <Mail className="w-4 h-4" />
                              Email
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">How Subscription Reminders Work</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>7 days before:</strong> First reminder email sent</li>
            <li>• <strong>3 days before:</strong> Second reminder email sent</li>
            <li>• <strong>1 day before:</strong> Final reminder email sent</li>
            <li>• <strong>After expiry:</strong> Subscription marked as expired</li>
          </ul>
          <p className="text-sm text-blue-700 mt-3">
            Click "Send Reminders" to manually trigger the reminder process, or set up a daily cron job to call <code className="bg-blue-100 px-1 rounded">/api/subscriptions/send-reminders</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsAdmin;
